/**
 * The issue console exists ONLY on dev. Not feature-flagged, not
 * permission-gated: on admin.rubytill.com the routes are never
 * registered, so there is nothing to reach.
 *
 * The suffix test is deliberately `.dev.rubytill.com` and not a substring
 * search for "dev" — `admin.devious.rubytill.com` is somebody else's host and
 * must NOT open a console that can drive an agent at the codebase. Staging is
 * excluded for the same reason: it is not dev.
 */
export function isDevHost(hostname: string = window.location.hostname): boolean {
  const h = hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1') return true;
  return h.endsWith('.dev.rubytill.com');
}

/**
 * Where the Console button points on a NON-dev host: the same issue's console
 * on the dev admin, in a new tab. The console itself still never runs here —
 * dev's login carries the user back to this path after sign-in, and shows a
 * "console runs only in DEV" notice while it does (LoginPage).
 *
 * Every row on a non-dev host was read from THAT host's own table, so it is
 * always stamped with the host's stage. The row's own `key`/`sourceStage` are
 * NOT used: the support API labels its local rows `dev` on every stage
 * (`mergeSources`), so prod rows arrived as `dev:<id>` and the dev console
 * opened them against the dev table — "nothing matches".
 */
export function devConsoleUrl(
  iss: { id: string; key?: string; sourceStage?: string | null },
  hostname: string = window.location.hostname,
): string {
  const h = hostname.toLowerCase();
  const apex = h.replace(/^(admin\.stage|admin\.dev|admin|stage|dev|www)\./, '');
  const hostStage = /(^|\.)stage\./.test(h) ? 'staging' : 'prod';
  const key = `${hostStage}:${iss.id}`;
  return `https://admin.dev.${apex}/app-issues/${encodeURIComponent(key)}/console`;
}

/** True when the login page was reached on the way to an issue console. */
export function isConsolePath(pathname: string): boolean {
  return /^\/app-issues\/[^/]+\/console\/?$/.test(pathname);
}
